import { t } from "/WebInterface/new-ui/modules/localizations/localizations.js";
import TooltipTemplate from "./template.js";
import { _$ } from "/WebInterface/new-ui/assets/js/utils/dom.js";

export default class Tooltip {
  constructor() {
    this.tooltipIdCounter = 0;
    this.styleElement = null;
    this.styleRules = new Map(); // Track rules by tooltip ID for cleanup
  }

  init = () => {
    const tooltipsElem = _$("[data-tooltip]").sel();

    tooltipsElem.forEach((element) => {
      this.addTooltip(element);
    });
  };

  // Create or get a stylesheet for dynamic tooltip styles (CSP-compliant)
  getStyleElement = () => {
    if (!this.styleElement) {
      this.styleElement = document.createElement("style");
      this.styleElement.setAttribute("data-tooltip-dynamic", "true");
      document.head.appendChild(this.styleElement);
    }
    return this.styleElement;
  };

  // Add CSS rule to stylesheet (CSP-compliant - no inline styles)
  addStyleRule = (tooltipId, rules) => {
    const styleEl = this.getStyleElement();
    const ruleText = `#${tooltipId} { ${rules} }`;
    
    // Store rule for cleanup
    if (!this.styleRules.has(tooltipId)) {
      this.styleRules.set(tooltipId, []);
    }
    this.styleRules.get(tooltipId).push(ruleText);
    
    // Append rule to stylesheet
    styleEl.textContent += ruleText + "\n";
  };

  // Remove CSS rules for a tooltip
  removeStyleRules = (tooltipId) => {
    if (!this.styleElement || !this.styleRules.has(tooltipId)) return;
    
    const rules = this.styleRules.get(tooltipId);
    let textContent = this.styleElement.textContent;
    
    // Remove all rules for this tooltip
    rules.forEach(rule => {
      textContent = textContent.replace(rule + "\n", "");
      textContent = textContent.replace(rule, "");
    });
    
    this.styleElement.textContent = textContent;
    this.styleRules.delete(tooltipId);
  };

  setTooltipPosition = (position, element, tooltip, fixedPosition = false) => {
    const { bottom, left, right, top } = element.getBoundingClientRect();
    const tooltipRect = tooltip.getBoundingClientRect();
    const centerX = left + (right - left) / 2 - tooltipRect.width / 2;
    const centerY = top + (bottom - top) / 2 - tooltipRect.height / 2;

    const setPosition = (position = "bottom") => {
      // Use stylesheet rules instead of inline styles (CSP-compliant - no style attribute)
      const tooltipEl = tooltip;
      const tooltipId = tooltipEl.id;
      if (!tooltipId) return;
      
      let rules = "";
      switch (position) {
        case "bottom":
          rules = `top: ${bottom + 10}px; left: ${centerX}px; right: auto; bottom: auto;`;
          break;
        case "top":
          const bottomVal = window.innerHeight - top + tooltipRect.height + 10;
          rules = `bottom: ${bottomVal}px; left: ${centerX}px; top: auto; right: auto;`;
          break;
        case "left":
          rules = `top: ${centerY}px; left: ${left - tooltipRect.width - 10}px; right: auto; bottom: auto;`;
          break;
        case "right":
          rules = `top: ${centerY}px; left: ${right + 10}px; right: auto; bottom: auto;`;
          break;
        default:
          break;
      }
      if (rules) {
        this.addStyleRule(tooltipId, rules);
      }
    };

    setPosition(position);
    if (fixedPosition) return;

    // Check appropriate position
    const positions = ["bottom", "top", "left", "right"];
    positions.every((pos) => {
      if (pos === position) return true;
      const { clientHeight, clientWidth } = document.body;

      const { left, right, top, bottom } = tooltip.getBoundingClientRect();

      if (left < 0 || top < 0 || right > clientWidth || bottom > clientHeight) {
        setPosition(pos);
        const arrow = _$(tooltip).find(".arrow").el();
        arrow.classList.remove(...positions);
        _$(arrow).addClass(pos);
        return true;
      } else {
        return false;
      }
    });
  };

  createTooltip = (
    element,
    tooltipText = "",
    position = "bottom",
    fixedPosition
  ) => {
    const text = tooltipText || element.getAttribute("data-tooltip");
    const dynamicData = element.getAttribute("data-tooltip-i18n-opt") || "{}";

    const tooltip = _$("<div>").addClass("text-tooltip");

    tooltip.html(
      TooltipTemplate({
        text: t(text, JSON.parse(dynamicData)),
        position,
      })
    );

    this.removeTooltip();
    
    // Generate unique ID for this tooltip instance
    const tooltipId = `tooltip-${Date.now()}-${++this.tooltipIdCounter}`;
    tooltip.attr("id", tooltipId);
    
    _$("body").append(tooltip.el());
    
    // Ensure tooltip is visible before positioning (needed for accurate getBoundingClientRect)
    const tooltipEl = tooltip.el();
    // Ensure display is block so it takes up space for measurements
    // Use utility class for display, stylesheet rules for positioning (no inline styles)
    tooltip.removeClass("d-n");
    tooltip.addClass("d-b");
    // Initialize positioning via stylesheet (CSP-compliant - no inline style attribute)
    this.addStyleRule(tooltipId, "top: 0px; left: 0px; opacity: 0;");

    _$(element).on("mouseleave", () => {
      this.removeStyleRules(tooltipId);
      tooltip.remove();
    });

    this.setTooltipPosition(position, element, tooltipEl, fixedPosition);
    
    // Make tooltip visible after positioning - CSS animation will handle the appearance
    // Update opacity via stylesheet rule (CSP-compliant - no inline style attribute)
    this.addStyleRule(tooltipId, "opacity: 1;");
  };

  addTooltip = ({ element, tooltipText, position, fixedPosition }) => {
    _$(element).on("mouseenter", () => {
      this.createTooltip(element, tooltipText, position, fixedPosition);
    });
  };

  removeTooltip = () => {
    const tooltips = _$(".text-tooltip").sel();
    tooltips.forEach((tooltip) => {
      const tooltipId = tooltip.id;
      if (tooltipId) {
        this.removeStyleRules(tooltipId);
      }
    });
    _$(".text-tooltip").remove();
  };
}
